import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type CurrentView = 'appointments' | 'campaigns' | 'crm' | 'dashboard' | 'settings';
export type AppTheme = 'light' | 'dark';

export interface UiState {
  // State
  sidebarCollapsed: boolean;
  mobileDrawerOpen: boolean;
  currentView: CurrentView;
  theme: AppTheme;

  // Actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileDrawer: (open: boolean) => void;
  setCurrentView: (view: CurrentView) => void;
  toggleTheme: () => void;
  setTheme: (theme: AppTheme) => void;
}

function applyTheme(theme: AppTheme) {
  if (typeof document !== 'undefined') {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }
}

function getInitialTheme(): AppTheme {
  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem('fresha-ui');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.state?.theme === 'dark' || parsed?.state?.theme === 'light') {
          return parsed.state.theme;
        }
      }
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    } catch {
      // ignore
    }
  }
  return 'light';
}

const initialTheme = getInitialTheme();
applyTheme(initialTheme);

export const useUiStore = create<UiState>()(
  devtools(
    persist(
      (set, get) => ({
        sidebarCollapsed: false,
        mobileDrawerOpen: false,
        currentView: 'appointments',
        theme: initialTheme,

        toggleSidebar: () =>
          set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

        setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

        setMobileDrawer: (mobileDrawerOpen) => set({ mobileDrawerOpen }),

        setCurrentView: (currentView) => set({ currentView }),

        toggleTheme: () => {
          const nextTheme = get().theme === 'dark' ? 'light' : 'dark';
          applyTheme(nextTheme);
          set({ theme: nextTheme });
        },

        setTheme: (theme) => {
          applyTheme(theme);
          set({ theme });
        },
      }),
      {
        name: 'fresha-ui',
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          theme: state.theme,
        }),
        onRehydrateStorage: () => (state) => {
          if (state?.theme) {
            applyTheme(state.theme);
          }
        },
      }
    ),
    { name: 'UiStore' }
  )
);
